import type { SceneryItem } from '../types/track';
import type { CircuitDesign, CircuitNode } from '../utils/circuitBuilder';

function node(x: number, y: number, heading: number, handle = 450): CircuitNode {
  return { x, y, heading, handle };
}

function scenery(
  prefix: string,
  entries: [SceneryItem['type'], string, number, number, number, number][],
  grove: { x: number; y: number }
): SceneryItem[] {
  return [
    ...entries.map(([type, label, x, y, scale, rotation], i) => ({
      id: `${prefix}-scenery-${i}`,
      type,
      label,
      position: { x, y },
      scale,
      rotation,
    })),
    ...Array.from({ length: 24 }, (_, i) => ({
      id: `${prefix}-tree-${i}`,
      type: 'tree',
      position: { x: grove.x + (i % 6) * 180, y: grove.y + Math.floor(i / 6) * 200 },
      scale: 1.4,
      rotation: 0,
    })),
  ];
}

// Original arcade interpretations: broad circuit character, not traced venue maps.
export const F1_EASTERN_DESIGNS: CircuitDesign[] = [
  {
    id: 'melbourne-grand-prix',
    name: 'Melbourne Grand Prix',
    category: 'Formula 1',
    description:
      'Albert Park-inspired lake sweeps, open fast bends and a broad lakeside direction change.',
    width: 11000,
    height: 10400,
    roadWidth: 240,
    difficulty: 'hard',
    nodes: [
      node(4000, 1500, 0),
      node(6500, 1500, 0, 550),
      node(8100, 2200, 50, 550),
      node(8500, 3500, 100),
      node(8000, 4600, 100),
      node(8500, 5700, 55),
      node(9400, 7000, 75, 550),
      node(8900, 8500, 140, 550),
      node(7300, 9000, 180, 550),
      node(5700, 8400, 210),
      node(4400, 8000, 170),
      node(3000, 8400, 180, 550),
      node(1600, 7400, 245, 550),
      node(1200, 5700, 270, 550),
      node(1800, 4200, 305, 550),
      node(2600, 3000, 300),
      node(2400, 2000, 280),
      node(3000, 1500, 0),
    ],
    scenery: scenery(
      'melbourne',
      [
        ['label', 'ALBERT PARK', 5200, 4800, 3, 0],
        ['label', 'LAKESIDE SWEEP', 3800, 7100, 1.4, 0],
        ['label', 'FAST PARK BENDS', 9000, 4400, 1.3, 0],
        ['water', 'ALBERT LAKE', 4900, 5900, 10, 0],
        ['building', 'LAKESIDE PITS', 4900, 1000, 4, 0],
        ['grandstand', 'PARK STRAIGHT', 4900, 2000, 4, 0],
        ['grandstand', 'LAKE VIEW', 7100, 9700, 3, 0],
      ],
      { x: 3400, y: 3500 }
    ),
  },
  {
    id: 'shanghai-grand-prix',
    name: 'Shanghai Grand Prix',
    category: 'Formula 1',
    description:
      'A tightening opening curl, rounded infield turns and a very long backstraight to a wide hairpin.',
    width: 11000,
    height: 10500,
    roadWidth: 240,
    difficulty: 'hard',
    nodes: [
      node(5000, 1400, 0),
      node(8000, 1400, 0, 550),
      node(9400, 2100, 60, 550),
      node(9200, 3100, 155),
      node(8200, 3500, 130),
      node(8100, 4600, 55),
      node(9300, 5900, 70, 550),
      node(9400, 7700, 100, 550),
      node(8400, 8700, 180, 550),
      node(7400, 8100, 235),
      node(6500, 6600, 205, 550),
      node(4700, 6400, 180),
      node(3600, 7200, 125),
      node(3000, 8500, 160, 550),
      node(1700, 8200, 250, 550),
      node(1500, 6500, 270, 550),
      node(1500, 2200, 270, 550),
      node(2300, 1200, 0, 550),
      node(3400, 1400, 0),
    ],
    scenery: scenery(
      'shanghai',
      [
        ['label', 'SHANGHAI', 5100, 3500, 3, 0],
        ['label', 'TIGHTENING FIRST TURN', 8600, 900, 1.3, 0],
        ['label', 'LONG BACKSTRAIGHT', 900, 4500, 1.4, 0],
        ['label', 'HAIRPIN BRAKING', 2100, 500, 1.3, 0],
        ['building', 'PADDOCK PAVILIONS', 5700, 900, 4, 0],
        ['grandstand', 'OPENING CURL', 10200, 2200, 3, Math.PI / 2],
        ['grandstand', 'BACKSTRAIGHT HAIRPIN', 2600, 1900, 3, 0],
        ['water', 'INFIELD POND', 5300, 4900, 6, 0],
      ],
      { x: 3800, y: 2300 }
    ),
  },
  {
    id: 'suzuka-grand-prix',
    name: 'Suzuka Grand Prix',
    category: 'Formula 1',
    description:
      'Esses, a generous hairpin, double-apex Spoon and 130R; the crossover is flattened and separated ' +
      'into non-crossing routing for the single-layer physics.',
    width: 11000,
    height: 10500,
    roadWidth: 220,
    difficulty: 'extreme',
    nodes: [
      node(5500, 1500, 0),
      node(8000, 1500, 0, 550),
      node(9300, 2300, 90, 550),
      node(9000, 3300, 135),
      node(8200, 4100, 90),
      node(8800, 5000, 90),
      node(8100, 5900, 120),
      node(8000, 7000, 45),
      node(9000, 7800, 90, 550),
      node(8500, 8800, 180, 550),
      node(7500, 8200, 220),
      node(6200, 7300, 180, 550),
      node(4600, 8200, 145, 550),
      node(3000, 9000, 180, 550),
      node(1600, 8200, 235, 550),
      node(1400, 6700, 285, 550),
      node(2200, 5400, 315, 550),
      node(3700, 4400, 315, 550),
      node(4400, 3400, 270),
      node(3500, 2500, 270),
      node(4000, 1500, 0, 550),
      node(4700, 1500, 0),
    ],
    scenery: scenery(
      'suzuka',
      [
        ['label', 'SUZUKA', 5900, 3900, 3, 0],
        ['label', 'S CURVES', 9800, 4500, 1.4, 0],
        ['label', 'HAIRPIN', 9200, 9400, 1.3, 0],
        ['label', 'SPOON', 2800, 9700, 1.4, 0],
        ['label', '130R', 2800, 2900, 1.4, 0],
        ['label', 'SEPARATED CROSSOVER', 4900, 5700, 1.2, 0],
        ['building', 'SUZUKA PITS', 6500, 1000, 4, 0],
        ['grandstand', 'MAIN STRAIGHT', 6500, 2000, 4, 0],
        ['grandstand', 'SPOON VIEW', 2400, 8100, 3, 0],
        ['park', 'CROSSOVER RESERVE', 5200, 6100, 5, 0],
      ],
      { x: 5200, y: 4500 }
    ),
  },
  {
    id: 'bahrain-grand-prix',
    name: 'Bahrain Grand Prix',
    category: 'Formula 1',
    description:
      'Desert straights linked by generous stop-and-go braking zones and a rolling technical infield.',
    width: 11000,
    height: 10000,
    roadWidth: 240,
    difficulty: 'hard',
    nodes: [
      node(4000, 1400, 0),
      node(8300, 1400, 0, 550),
      node(9400, 2300, 90, 550),
      node(8500, 3300, 160, 550),
      node(6900, 3700, 110),
      node(7100, 4900, 35),
      node(8600, 5500, 60, 550),
      node(9200, 6900, 90, 550),
      node(8200, 8200, 180, 550),
      node(5700, 8200, 180, 550),
      node(4400, 7300, 235, 550),
      node(3800, 5800, 235),
      node(2600, 5200, 180),
      node(1400, 4300, 270, 550),
      node(1400, 2800, 270),
      node(1900, 1800, 315),
      node(3100, 1400, 0),
    ],
    scenery: scenery(
      'bahrain',
      [
        ['label', 'BAHRAIN', 5000, 4400, 3, 0],
        ['label', 'DESERT BRAKING ZONE', 8500, 800, 1.3, 0],
        ['label', 'INFIELD TRACTION', 6100, 5600, 1.3, 0],
        ['label', 'BACKSTRAIGHT', 6400, 8900, 1.4, 0],
        ['sand', 'DESERT INFIELD', 5400, 6200, 8, 0],
        ['building', 'SAKHIR PIT COMPLEX', 5200, 900, 4, 0],
        ['grandstand', 'FIRST TURN', 10100, 2300, 3, Math.PI / 2],
        ['grandstand', 'DESERT OASIS', 7300, 7600, 3, 0],
      ],
      { x: 4400, y: 2300 }
    ),
  },
  {
    id: 'jeddah-grand-prix',
    name: 'Jeddah Grand Prix',
    category: 'Formula 1',
    description:
      'Counterclockwise coastal racing with high-speed linked sweeps and a spacious northern hairpin.',
    width: 9000,
    height: 13000,
    roadWidth: 240,
    difficulty: 'extreme',
    nodes: [
      node(4100, 11200, 0),
      node(6200, 11200, 0, 550),
      node(7500, 10300, 270, 550),
      node(6900, 8900, 245, 550),
      node(7200, 7500, 290, 550),
      node(6600, 6200, 250, 550),
      node(7000, 4800, 290, 550),
      node(6300, 3400, 245, 550),
      node(6200, 1900, 270, 550),
      node(5100, 900, 180, 550),
      node(4000, 1900, 90, 550),
      node(3800, 3400, 110, 550),
      node(2800, 4900, 115, 550),
      node(2400, 6500, 90, 550),
      node(3000, 8000, 65, 550),
      node(2500, 9400, 105, 550),
      node(2200, 10400, 70, 550),
      node(3100, 11200, 0, 550),
    ],
    scenery: scenery(
      'jeddah',
      [
        ['label', 'JEDDAH CORNICHE', 4600, 5700, 2.6, 0],
        ['label', 'COASTAL SWEEPS', 1100, 6700, 1.3, 0],
        ['label', 'NORTHERN HAIRPIN', 5100, 350, 1.3, 0],
        ['water', 'RED SEA', 1000, 4300, 8, 0],
        ['building', 'CORNICHE PITS', 4700, 11700, 4, 0],
        ['grandstand', 'SEAFRONT STRAIGHT', 4700, 10700, 4, 0],
        ['grandstand', 'HAIRPIN VIEW', 5100, 2200, 3, 0],
      ],
      { x: 4100, y: 7100 }
    ),
  },
  {
    id: 'singapore-grand-prix',
    name: 'Singapore Grand Prix',
    category: 'Formula 1',
    description:
      'Counterclockwise Marina Bay-inspired angular streets with the current shortened final sector ' +
      'represented by a direct waterfront run, not the former stadium detour.',
    width: 12000,
    height: 10500,
    roadWidth: 220,
    difficulty: 'extreme',
    nodes: [
      node(4700, 9000, 0),
      node(8400, 9000, 0, 550),
      node(9600, 8000, 270, 550),
      node(9600, 6200, 270),
      node(8700, 5300, 180),
      node(7600, 5300, 180),
      node(6700, 4300, 270, 550),
      node(6700, 2800, 270),
      node(5700, 1700, 180, 550),
      node(4100, 1700, 180),
      node(3100, 2700, 90, 550),
      node(3100, 4300, 90),
      node(2100, 5300, 180, 550),
      node(1200, 6300, 90, 550),
      node(1200, 8000, 90),
      node(2300, 9000, 0, 550),
      node(3800, 9000, 0),
    ],
    scenery: scenery(
      'singapore',
      [
        ['label', 'MARINA BAY', 4800, 5900, 3, 0],
        ['label', 'CIVIC DISTRICT', 4600, 3000, 1.3, 0],
        ['label', 'SHORTENED WATERFRONT SECTOR', 2900, 7900, 1.2, 0],
        ['water', 'MARINA RESERVOIR', 7700, 7200, 7, 0],
        ['building', 'BAYFRONT PITS', 5600, 9500, 4, 0],
        ['building', 'CIVIC HALL', 4800, 3700, 3, 0],
        ['grandstand', 'PIT STRAIGHT', 5600, 8500, 4, 0],
        ['grandstand', 'STREET JUNCTION', 10300, 6500, 3, Math.PI / 2],
      ],
      { x: 3800, y: 4400 }
    ),
  },
  {
    id: 'lusail-grand-prix',
    name: 'Lusail Grand Prix',
    category: 'Formula 1',
    description:
      'A long opening straight releases into flowing desert sweeps and linked medium-speed arcs.',
    width: 11000,
    height: 10000,
    roadWidth: 240,
    difficulty: 'hard',
    nodes: [
      node(4100, 1400, 0),
      node(8100, 1400, 0, 550),
      node(9500, 2400, 80, 550),
      node(9000, 3900, 135, 550),
      node(8000, 5000, 100, 550),
      node(8400, 6400, 65, 550),
      node(8000, 7800, 145, 550),
      node(6500, 8500, 180, 550),
      node(4900, 8000, 205, 550),
      node(3500, 8300, 180, 550),
      node(2000, 7500, 235, 550),
      node(1400, 6000, 270, 550),
      node(2000, 4700, 315, 550),
      node(2400, 3500, 270, 550),
      node(2200, 2500, 270),
      node(2500, 1700, 315),
      node(3200, 1400, 0),
    ],
    scenery: scenery(
      'lusail',
      [
        ['label', 'LUSAIL', 5500, 4600, 3, 0],
        ['label', 'FLOWING DESERT SWEEP', 9400, 5700, 1.3, 0],
        ['label', 'LINKED ARCS', 5400, 9200, 1.3, 0],
        ['sand', 'QATAR DESERT', 5700, 6300, 7, 0],
        ['building', 'LUSAIL PIT BUILDING', 5500, 900, 4, 0],
        ['grandstand', 'MAIN STRAIGHT', 5500, 1900, 4, 0],
        ['grandstand', 'SWEEP VIEW', 7300, 7300, 3, 0],
      ],
      { x: 4400, y: 2900 }
    ),
  },
  {
    id: 'yas-marina-grand-prix',
    name: 'Yas Marina Grand Prix',
    category: 'Formula 1',
    description:
      'Counterclockwise racing with a revised fast hairpin, long acceleration zones and a flowing ' +
      'hotel sector; no old hairpin chicane or grade-separated scenery is simulated.',
    width: 12000,
    height: 11000,
    roadWidth: 240,
    difficulty: 'hard',
    nodes: [
      node(4800, 9300, 0),
      node(7300, 9300, 0, 550),
      node(8600, 8600, 315, 550),
      node(9600, 7400, 270, 550),
      node(9000, 6200, 235),
      node(9300, 5000, 285),
      node(8800, 3700, 245, 550),
      node(8200, 2200, 270, 550),
      node(6900, 1200, 180, 550),
      node(5600, 2200, 90, 550),
      node(5300, 3500, 135, 550),
      node(3400, 4100, 180, 550),
      node(1600, 4100, 180, 550),
      node(900, 5200, 90, 550),
      node(1600, 6400, 35, 550),
      node(2900, 7000, 60, 550),
      node(3000, 8400, 90, 550),
      node(3900, 9300, 0, 550),
    ],
    scenery: scenery(
      'yas-marina',
      [
        ['label', 'YAS MARINA', 6000, 5600, 3, 0],
        ['label', 'REVISED FAST HAIRPIN', 6900, 600, 1.3, 0],
        ['label', 'LONG ACCELERATION ZONE', 3400, 3500, 1.3, 0],
        ['label', 'HOTEL SECTOR', 10300, 6500, 1.3, 0],
        ['water', 'YAS HARBOUR', 6600, 7600, 7, 0],
        ['building', 'MARINA HOTEL', 8400, 7100, 3, Math.PI / 2],
        ['building', 'YAS PIT COMPLEX', 5500, 9800, 4, 0],
        ['grandstand', 'MARINA STRAIGHT', 5500, 8800, 4, 0],
        ['grandstand', 'HAIRPIN TERRACE', 6900, 2600, 3, 0],
      ],
      { x: 4400, y: 6400 }
    ),
  },
];
